import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Megaphone, Loader2 } from "lucide-react";

export type BroadcastAudience = "all" | "pro" | "free" | "vip";

export interface PlatformBroadcastRow {
  id: string;
  title: string;
  body: string | null;
  audience: string;
  created_at: string;
  recipient_count: number;
}

const STALE_MS = 2 * 60 * 1000;

export function BroadcastsPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<BroadcastAudience>("all");
  const [sending, setSending] = useState(false);

  const { data: broadcasts, isLoading } = useQuery({
    queryKey: ["founder-platform-broadcasts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_broadcasts")
        .select("id, title, body, audience, created_at, recipient_count")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data ?? []) as PlatformBroadcastRow[];
    },
    staleTime: STALE_MS,
  });

  const sendBroadcast = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      toast({ title: "Title required", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.rpc("founder_send_broadcast", {
        p_title: t,
        p_body: body.trim() || null,
        p_audience: audience,
      });
      if (error) throw error;
      const result = data as { recipient_count?: number } | null;
      toast({
        title: "Broadcast sent",
        description: `Delivered to ${result?.recipient_count ?? 0} user(s).`,
      });
      setTitle("");
      setBody("");
      await queryClient.invalidateQueries({ queryKey: ["founder-platform-broadcasts"] });
    } catch (err) {
      toast({
        title: "Could not send broadcast",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="founder-broadcasts-panel">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5" /> Send broadcast
                </CardTitle>
                <CardDescription>
                  Creates an in-app notification (bell) for each user in the selected audience.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={sendBroadcast} className="space-y-4 max-w-xl">
                  <div className="space-y-2">
                    <Label htmlFor="bc-title">Title</Label>
                    <Input
                      id="bc-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Short headline"
                      maxLength={200}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bc-body">Message (optional)</Label>
                    <Textarea
                      id="bc-body"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Details shown in the notification"
                      rows={4}
                      maxLength={2000}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Audience</Label>
                    <Select value={audience} onValueChange={(v) => setAudience(v as BroadcastAudience)}>
                      <SelectTrigger className="w-full sm:w-[280px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All users</SelectItem>
                        <SelectItem value="pro">Pro (active Solo / Growth / Grace)</SelectItem>
                        <SelectItem value="free">Free (non‑Pro, non‑VIP)</SelectItem>
                        <SelectItem value="vip">VIP only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" disabled={sending}>
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Send broadcast"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent broadcasts</CardTitle>
                <CardDescription>History of messages you&apos;ve sent.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : broadcasts?.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Audience</TableHead>
                          <TableHead>Recipients</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {broadcasts.map((b) => (
                          <TableRow key={b.id}>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {new Date(b.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell className="font-medium">{b.title}</TableCell>
                            <TableCell className="uppercase text-xs">{b.audience}</TableCell>
                            <TableCell>{b.recipient_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No broadcasts yet.</p>
                )}
              </CardContent>
            </Card>
    </div>
  );
}
